import * as React from 'react';
import {
  getLastestSurveyVersionCount,
  getOldSurveyCount,
  getSurveys,
} from '../fhirSearchQuery';
import { useActiveProject } from '../../../hooks';
import useService from '../../../hooks/useService';
import FHIRSearchService from '../../../services/FhirSearchService';
import useSurveysList from './useSurveysList';
import useToggle from '../../../hooks/useToggle';

const PAGE_SIZE = 10;

const fhirSearchService = new FHIRSearchService();

const escapeSearchValue = (searchValue: string) =>
  searchValue.replace(/[/\\:+\-=!?"~^${}()|[\]]/g, '\\$&').replace(/[<>]/g, '');

export const useSurveysAndCount = () => {
  const [queryProject] = useService(fhirSearchService.search);
  const [activeProject] = useActiveProject();
  const { statusFilters, setSurveyCount, searchValue } = useSurveysList();
  const escapedSearchValue = escapeSearchValue(searchValue);

  const status = statusFilters.includes('all') ? undefined : statusFilters;

  const [isLoading, setIsLoading, setLoadingFinished] = useToggle(false);
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [hits, setHits] = React.useState<number | null>(null);
  const [results, setResults] = React.useState([]);

  const listUpdateCount = React.useRef(0);

  const makeFetchRequest = React.useCallback(async () => {
    listUpdateCount.current++;
    const updateCountBeforeLoad = listUpdateCount.current;

    setIsLoading();
    const response = await queryProject(
      activeProject.id,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      getSurveys(status, escapedSearchValue, 0),
      undefined,
      { queryId: 'surveys-list-initial' },
    );

    if (updateCountBeforeLoad === listUpdateCount.current) {
      setHits(response?.data?.hits?.total?.value);
      setPageIndex(1);
      setResults(response?.data?.hits?.hits?.map((_: any) => _._source));
    }
    setLoadingFinished();
  }, [
    activeProject.id,
    escapedSearchValue,
    status,
    queryProject,
    setIsLoading,
    setLoadingFinished,
  ]);

  const loadMore = React.useCallback(async () => {
    const updateCountBeforeLoad = listUpdateCount.current;

    setIsLoading();
    const response = await queryProject(
      activeProject.id,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      getSurveys(status, escapedSearchValue, pageIndex),
      undefined,
      { queryId: 'surveys-list-pagination' },
    );

    if (updateCountBeforeLoad === listUpdateCount.current) {
      setPageIndex(pageIndex + 1);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setResults(prevResults => [
        ...prevResults,
        ...response?.data?.hits?.hits?.map((_: any) => _._source),
      ]);
    }
    setLoadingFinished();
  }, [
    activeProject.id,
    pageIndex,
    escapedSearchValue,
    status,
    queryProject,
    setIsLoading,
    setLoadingFinished,
  ]);

  const fetchCounts = React.useCallback(async () => {
    // Since older surveys don't have a primary-survey-id tag, we need to do two separate queries
    const countResponse = await Promise.all([
      queryProject(
        activeProject.id,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        getOldSurveyCount(status, escapedSearchValue),
        undefined,
        { queryId: 'surveys-list-old-count' },
      ),
      queryProject(
        activeProject.id,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        getLastestSurveyVersionCount(status, escapedSearchValue),
        undefined,
        { queryId: 'surveys-list-latest-version-count' },
      ),
    ]);

    const filterHits = (hit: {
      _source: {
        meta: {
          tag: [{ system: string; code: string }];
        };
      };
    }) => {
      return hit?._source?.meta.tag.filter(
        tag => tag.system === 'http://lifeomic.com/fhir/primary-survey-id',
      )[0].code;
    };

    const oldSurveyCount = countResponse[0]?.data?.aggregations?.total?.value;
    const primarySurveyIds =
      countResponse[1]?.data?.hits?.hits.map(filterHits) || [];

    const count = oldSurveyCount + new Set(primarySurveyIds).size;

    setSurveyCount(count || 0);
  }, [
    activeProject.id,
    queryProject,
    escapedSearchValue,
    setSurveyCount,
    status,
  ]);

  const resetResults = () => {
    setLoadingFinished();
    setResults([]);
    setPageIndex(0);
    setHits(null);
  };

  React.useEffect(() => {
    if (!activeProject.id) return;

    setResults([]);
    makeFetchRequest();
    fetchCounts();
  }, [activeProject.id, makeFetchRequest, fetchCounts]);

  return {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    hasMoreToLoad: pageIndex * PAGE_SIZE < hits,
    isLoading,
    loadMore,
    results,
    resetResults,
    makeFetchRequest,
  };
};
